import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { ImageType, ImageUpload } from "components/ImageUpload/ImageUpload";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { getEnvVariable } from "utilities/env";

interface Props {
  closeModal: () => void;
  isOpen: boolean;
  data: {
    id: string;
  };
}

export const ManageImages = ({ closeModal, isOpen, data }: Props) => {
  const [images, setImages] = useState<ImageType[]>([]);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { removePhoto, setPhoto, useRewardData } = useReward();
  const { data: rewardData } = useRewardData(data.id as string);
  const { code } = useAppSelector(selectCurrentStoreData);
  const { notify } = useNotifications();
  const { message } = useMessage();

  const saveImages = async () => {
    setIsSubmiting(true);

    return await Promise.allSettled(
      images
        .filter((image) => image.status !== "saved")
        .map(async (image: ImageType) => {
          switch (image.status) {
            case "new":
              return await setPhoto(data.id, image.file);
            case "deleted":
              return await removePhoto(data.id, image.id as string);
          }
        }),
    ).then(() => {
      notify(
        message({
          id: "rewardUpdated",
        }),
      );
      setIsSubmiting(false);
      closeModal();
    });
  };

  useEffect(() => {
    setImages(
      rewardData?.photos
        ? rewardData.photos.map(({ id }: { id: string }) => {
            const imgUrl = `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/reward/${rewardData.rewardId}/photo/${id}`;

            return {
              path: imgUrl,
              preview: imgUrl,
              status: "saved",
              id: id,
            };
          })
        : [],
    );
  }, [rewardData, code]);

  return (
    <OLModal
      header={message({
        id: "manageImages",
      })}
      cancelText={message({ id: "cancel" })}
      submitText={message({ id: "saveChanges" })}
      onClose={closeModal}
      open={isOpen}
      modalWidth={860}
      submitAction={saveImages}
      submitDisabled={isSubmiting}
    >
      {rewardData && <ImageUpload setImages={setImages} images={images} />}
    </OLModal>
  );
};
